// Suggests which library plan a client's plan should renew onto. The library
// holds one plan row per plan year, so next year's version of a plan is a
// different row whose name is close to — but rarely identical to — the one the
// client is on ("Kaiser Platinum 90 HMO 0/20 2025" vs "KP Platinum 90 HMO
// 0/20 PCP"). Pure token scoring: no DB, same result on server and client.

/** Lowercased alphanumeric tokens, minus plan-year tokens (2019–2099). */
export function planNameTokens(name: string | null | undefined): string[] {
  return String(name ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0 && !/^20[1-9]\d$/.test(t));
}

/** Dice coefficient over the two names' token multisets, 0..1. */
export function planNameSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  const ta = planNameTokens(a);
  const tb = planNameTokens(b);
  if (ta.length === 0 || tb.length === 0) return 0;
  const remaining = new Map<string, number>();
  for (const t of tb) remaining.set(t, (remaining.get(t) ?? 0) + 1);
  let shared = 0;
  for (const t of ta) {
    const left = remaining.get(t) ?? 0;
    if (left > 0) {
      shared++;
      remaining.set(t, left - 1);
    }
  }
  return (2 * shared) / (ta.length + tb.length);
}

/** A candidate at or above this score is offered as the suggested match. */
export const RENEWAL_MATCH_SUGGEST_THRESHOLD = 0.6;

export interface RenewalCandidateLike {
  id: number;
  name: string;
  carrierId?: number | null;
  type?: string | null;
}

/**
 * Candidates for renewing `current`, best first. Only the same carrier and
 * line of coverage are considered — a name match across carriers is never a
 * renewal of the same plan.
 */
export function rankRenewalCandidates<T extends RenewalCandidateLike>(
  current: { name: string; carrierId?: number | null; type?: string | null },
  candidates: T[],
): Array<T & { score: number }> {
  const type = String(current.type ?? "").toLowerCase();
  return candidates
    .filter(
      (c) => c.carrierId === current.carrierId && String(c.type ?? "").toLowerCase() === type,
    )
    .map((c) => ({ ...c, score: planNameSimilarity(current.name, c.name) }))
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}
